import { ref, computed, watch, nextTick } from 'vue'
import urljoin from 'url-join'
import { storeToRefs } from 'pinia'
import { useAuthStore, AUTH_STATE } from '@/stores/auth/auth'
import { useMainStore } from '@/stores/main'
import { storage } from '@/utils/storage'
import { authApi, userApi } from '@/api'

export const useAuth = () => {
  const authStore = useAuthStore()
  const mainStore = useMainStore()
  const nuxtApp = useNuxtApp()
  const { $request, $getDeviceToken, $bridge } = nuxtApp
  const { apiUrl, clientId } = useRuntimeConfig().public
  const { fetchSiteConfig, dtVerify } = useSite()
  const authToken = useAuthToken()
  const { getUserInfo, setUserInfo, fetchUserInfo: userInfoFetchUserInfo } = useUserInfo({ autoInit: false })
  const { token, isLoggedIn, setToken, extendToken, clearToken } = authToken
  const { reset } = useAuthOtp()
  const { setModal } = mainStore

  // 從 authStore 解構狀態
  const { authState, isPlaying, loginData, pollingTimer, deviceTokenChecked } = storeToRefs(authStore)

  // 保留在 composable 的本地狀態（業務邏輯相關）
  const tick = ref(2000)

  const isInline = computed(() => getUserInfo.value?.rank >= 0)
  const isNotInline = computed(() => !(getUserInfo.value?.rank >= 0))
  const isAuthenticated = computed(
    () =>
      authState.value === AUTH_STATE.AUTHENTICATED ||
      authState.value === AUTH_STATE.LOGIN_SUCCESS ||
      authState.value === AUTH_STATE.TOKEN_AUTHENTICATED ||
      authState.value === AUTH_STATE.INLINE ||
      authState.value === AUTH_STATE.LOGIN
  )

  const fetchUserInfo = async () => {
    try {
      const userData = await userInfoFetchUserInfo()

      if (nuxtApp.$sentry) {
        nuxtApp.$sentry.setUser({ id: userData.uid_site })
      }

      return userData
    } catch (error) {
      console.error('[useAuth] fetchUserInfo error:', error)
      throw error
    }
  }

  const checkDeviceToken = async () => {
    try {
      if (!dtVerify.value) {
        return
      }

      const { exist } = await $request.send(userApi.checkDt())
      if (!exist) {
        const dt = await $getDeviceToken()

        if (dt) {
          if (nuxtApp.$sentry) {
            nuxtApp.$sentry.setTag('dt', dt)
          }
          await $request.send(userApi.updateDt({ dt }))
        }
      }
    } catch (error) {
      console.error('[useAuth] checkDeviceToken error:', error)
      throw error
    }
  }

  const authenticate = async (data) => {
    try {
      const authData = { ...loginData.value, ...data }
      const response = await $request.send(authApi.login(authData))

      setToken(response.token)

      return response.token
    } catch (error) {
      console.error('[useAuth] authenticate error:', error)
      clearToken()
      authState.value = AUTH_STATE.IDLE
      throw error
    }
  }

  const authenticateApp = async (data) => {
    try {
      const response = await $request.send(authApi.loginApp(data))

      setToken(response.token)

      return response.token
    } catch (error) {
      console.error('[useAuth] authenticateApp error:', error)
      clearToken()
      authState.value = AUTH_STATE.IDLE
      throw error
    }
  }

  const logout = async () => {
    try {
      if (token.value) {
        await $request.send(authApi.logout())
      }
    } catch (error) {
      console.error('[useAuth] logout error:', error)
    } finally {
      if ($bridge?.available) {
        $bridge.callHandler('resetAuth', null, () => {
          console.log('[Bridge] Auth Reset')
        })
      }
      reset()
      clearToken()
      setUserInfo({})
      authState.value = AUTH_STATE.IDLE
      deviceTokenChecked.value = false
      setModal(null)
      stopPolling()
    }
  }

  const startPolling = () => {
    if (!import.meta.client) {
      return
    }

    stopPolling()

    const pollInterval = tick.value * (isPlaying.value ? 5 : 1)

    pollingTimer.value = setInterval(async () => {
      try {
        if (!isLoggedIn.value) {
          stopPolling()
          return
        }
        await fetchUserInfo()
        extendToken()
      } catch (error) {
        console.error('[useAuth] polling error:', error)
        stopPolling()
        await logout()
      }
    }, pollInterval)
  }

  const stopPolling = () => {
    if (!import.meta.client) {
      return
    }

    if (pollingTimer.value) {
      clearInterval(pollingTimer.value)
      pollingTimer.value = null
    }
  }

  const completeLoginFlow = async () => {
    authState.value = AUTH_STATE.AUTHENTICATED
    await fetchUserInfo()

    authState.value = AUTH_STATE.LOGIN_SUCCESS
    await nextTick()

    if (isInline.value) {
      authState.value = AUTH_STATE.INLINE
    } else {
      authState.value = AUTH_STATE.LOGIN
    }
    startPolling()
  }

  const loginWithToken = async (newToken) => {
    try {
      if (newToken) {
        setToken(newToken)
      }

      if (isLoggedIn.value) {
        authState.value = AUTH_STATE.TOKEN_AUTHENTICATED
        await fetchUserInfo()
        // extendToken()

        if (isInline.value) {
          authState.value = AUTH_STATE.INLINE
        } else {
          authState.value = AUTH_STATE.LOGIN
        }
        startPolling()
      }
    } catch (error) {
      console.error('[useAuth] loginWithToken error:', error)
      clearToken()
      authState.value = AUTH_STATE.IDLE
      throw error
    }
  }

  watch(authState, async (newState) => {
    if (newState === AUTH_STATE.LOGIN && !deviceTokenChecked.value) {
      deviceTokenChecked.value = true
      await checkDeviceToken()
    }
  })

  const loginWithId = async ({ username, password }) => {
    try {
      loginData.value = {
        provider: 'local',
        username,
        password,
      }

      authState.value = AUTH_STATE.AUTHENTICATING
      await authenticate()

      await completeLoginFlow()
    } catch (error) {
      console.error('[useAuth] loginWithId error:', error)
      throw error
    }
  }

  const loginWithPhone = async ({ countryCode: code, phone, password }) => {
    try {
      loginData.value = {
        provider: 'phone',
        countryCode: code,
        phone,
        password,
      }

      authState.value = AUTH_STATE.AUTHENTICATING
      await authenticate()

      await completeLoginFlow()
    } catch (error) {
      console.error('[useAuth] loginWithPhone error:', error)
      throw error
    }
  }

  const loginWithEmail = async ({ email, password }) => {
    try {
      loginData.value = {
        provider: 'email',
        email,
        password,
      }

      authState.value = AUTH_STATE.AUTHENTICATING
      await authenticate()

      await completeLoginFlow()
    } catch (error) {
      console.error('[useAuth] loginWithEmail error:', error)
      throw error
    }
  }

  const loginWithTelegram = async ({ loginType, additionalUserInfo: addInfo, isSignature }) => {
    try {
      loginData.value = {
        provider: 'telegram',
        loginType,
        additionalUserInfo: addInfo,
        isSignature,
      }

      authState.value = AUTH_STATE.AUTHENTICATING
      await authenticate()

      await completeLoginFlow()
    } catch (error) {
      console.error('[useAuth] loginWithTelegram error:', error)
      throw error
    }
  }

  const loginWithApp = async (data) => {
    try {
      authState.value = AUTH_STATE.AUTHENTICATING
      await authenticateApp(data)

      await completeLoginFlow()
    } catch (error) {
      console.error('[useAuth] loginWithApp error:', error)
      throw error
    }
  }

  const redirectTo3Party = async ({ provider, fc: friendCode }) => {
    try {
      const dt = dtVerify.value ? await $getDeviceToken() : null

      if (dt && nuxtApp.$sentry) {
        nuxtApp.$sentry.setTag('dt', dt)
      }

      const affiliateCode = storage.get('ac')
      const nickname = storage.get('nk')

      const messages = {
        url: window.location.href,
        affiliateCode,
        nickname,
      }

      if (nuxtApp.$sentry) {
        nuxtApp.$sentry.captureMessage(JSON.stringify(messages))
      }

      const referralObj = {}
      if (![undefined, 'undefined', null, 'null'].includes(affiliateCode)) {
        referralObj.ac = affiliateCode
      }

      if (![undefined, 'undefined', null, 'null'].includes(nickname)) {
        referralObj.nick_name = nickname
      }

      window.location.href = urljoin(
        apiUrl,
        'v2',
        clientId,
        'login?' +
          new URLSearchParams({
            provider,
            redirectURI: `${window.location.protocol}//${window.location.host}`,
            ...referralObj,
            dt,
            platform: 'pwa',
            fc: friendCode,
          }).toString()
      )
    } catch (error) {
      console.error('[useAuth] redirectTo3Party error:', error)
      throw error
    }
  }

  const setPlay = (playing) => {
    isPlaying.value = playing
    if (pollingTimer.value) {
      // stopPolling()
      startPolling()
    }
  }

  return {
    authState,
    isPlaying,
    isInline,
    isNotInline,
    isAuthenticated,

    loginWithToken,
    loginWithId,
    loginWithPhone,
    loginWithEmail,
    loginWithTelegram,
    loginWithApp,
    redirectTo3Party,
    logout,
    setPlay,
    fetchUserInfo,
  }
}
